<template>
  <div
    id="pedido-table"
    ref="tableContainer"
    class="pedido-table"
    :style="{ height: `${tableHeight}px` }"
  >
    <div
      id="table-scroll"
      class="table-scroll"
    >
      <OrderImportItemAOp
        v-for="group in groupedOrders"
        :key="group.groupKey"
        :orderGroup="group"
        @validation-change="handleValidationChange"
        @cliente-change="handleClienteChange"
        @vendedor-change="handleVendedorChange"
        @order-change="handleOrderChange"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeMount,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import OrderImportItemAOp from './order_import_item_a_op.vue';
import { useOrderImportStore } from '@/views/order_import/stores/order_import_store';

const orderImportStore = useOrderImportStore();
const { selectedItems } = storeToRefs(orderImportStore);

const emit = defineEmits(['all-valid']);

const tableHeight = ref(200);
const validationState = ref(new Map());

const groupedOrders = computed(() => {
  const groups = new Map();

  selectedItems.value.forEach((item) => {
    const groupKey = item.codigoClientePedido || `no-cliente-${item.codigoCantidad}`;

    if (!groups.has(groupKey)) {
      const firstItemWithCliente = selectedItems.value.find(
        (i) =>
          (i.codigoClientePedido === groupKey ||
            (!i.codigoClientePedido && groupKey.startsWith('no-cliente'))) &&
          i.Cliente &&
          i.Cliente.Codigo > 0,
      );

      const firstItemWithVendedor = selectedItems.value.find(
        (i) =>
          (i.codigoClientePedido === groupKey ||
            (!i.codigoClientePedido && groupKey.startsWith('no-cliente'))) &&
          i.Vendedor &&
          i.Vendedor.Codigo > 0,
      );

      groups.set(groupKey, {
        groupKey,
        codigoClientePedido: item.codigoClientePedido,
        descripcionCliente: item.descripcionCliente,
        descripcionVendedor: item.descripcionVendedor,
        Cliente: firstItemWithCliente?.Cliente || null,
        Vendedor: firstItemWithVendedor?.Vendedor || null,
        orders: [],
      });
    }

    groups.get(groupKey).orders.push(item);
  });

  return Array.from(groups.values());
});

const handleClienteChange = ({ groupKey, cliente }) => {
  const nextSelected = selectedItems.value.map((item) => {
    const itemGroupKey = item.codigoClientePedido || `no-cliente-${item.codigoCantidad}`;
    if (itemGroupKey !== groupKey) return item;
    return { ...item, Cliente: cliente };
  });
  orderImportStore.setSelectedItems(nextSelected);
};

const handleVendedorChange = ({ groupKey, vendedor }) => {
  const nextSelected = selectedItems.value.map((item) => {
    const itemGroupKey = item.codigoClientePedido || `no-cliente-${item.codigoCantidad}`;
    if (itemGroupKey !== groupKey) return item;
    return { ...item, Vendedor: vendedor };
  });
  orderImportStore.setSelectedItems(nextSelected);
};

const handleOrderChange = ({ orderCodigoCantidad, changes }) => {
  const nextSelected = selectedItems.value.map((item) => {
    if (item.codigoCantidad !== orderCodigoCantidad) return item;
    return { ...item, ...changes };
  });
  orderImportStore.setSelectedItems(nextSelected);
};

const handleValidationChange = ({ groupKey, isValid }) => {
  validationState.value.set(groupKey, isValid);
  checkAllValid();
};

const checkAllValid = () => {
  const allValid =
    groupedOrders.value.length > 0 &&
    groupedOrders.value.every((group) => validationState.value.get(group.groupKey) === true);
  emit('all-valid', allValid);
};

const calculateTableHeight = () => {
  const modalDialog = document.querySelector('.el-dialog');
  if (modalDialog) {
    const modalHeight = modalDialog.offsetHeight;
    const headerHeight = 80;
    tableHeight.value = modalHeight - headerHeight - 40;
  }
};

onMounted(() => {
  calculateTableHeight();
});
</script>

<style scoped>
.pedido-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
</style>
